import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Email';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { vehiclesApi } from '../services/api';
import { VehicleInspection, VehicleInspectionCreate } from '../types';
import VehicleInspectionForm from '../components/VehicleInspectionForm';
import VehicleInspectionTable from '../components/VehicleInspectionTable';

const VehicleDocs: React.FC = () => {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editingInspection, setEditingInspection] = useState<VehicleInspection | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportResult, setExportResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);

  // Fetch pending inspections
  const { data, isLoading, error } = useQuery({
    queryKey: ['vehicle-inspections', false],
    queryFn: () => vehiclesApi.getInspections({ is_exported: false }),
  });

  // Create inspection mutation
  const createMutation = useMutation({
    mutationFn: (data: VehicleInspectionCreate) => vehiclesApi.createInspection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-vehicle-inspections'] });
      setFormOpen(false);
      setEditingInspection(null);
      setSaveResult({
        success: true,
        message: 'Vehicle inspection saved successfully!',
      });
    },
    onError: (error: any) => {
      console.error('Error creating inspection:', error);
      setSaveResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to save inspection. Please try again.',
      });
    },
  });

  // Update inspection mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VehicleInspectionCreate }) =>
      vehiclesApi.updateInspection(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-vehicle-inspections'] });
      setFormOpen(false);
      setEditingInspection(null);
      setSaveResult({
        success: true,
        message: 'Vehicle inspection updated successfully!',
      });
    },
    onError: (error: any) => {
      console.error('Error updating inspection:', error);
      setSaveResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to update inspection. Please try again.',
      });
    },
  });

  // Delete inspection mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => vehiclesApi.deleteInspection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-inspections'] });
    },
  });

  // Export and email mutation
  const exportMutation = useMutation({
    mutationFn: () => vehiclesApi.exportAndEmail(),
    onSuccess: (result) => {
      setExportResult({
        success: result.success,
        message: result.message,
      });
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['vehicle-inspections'] });
      }
    },
    onError: (error: any) => {
      setExportResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to export inspections',
      });
    },
  });

  const handleAddUnit = () => {
    setEditingInspection(null);
    setFormOpen(true);
  };

  const handleEdit = (inspection: VehicleInspection) => {
    setEditingInspection(inspection);
    setFormOpen(true);
  };

  const handleFormSubmit = (formData: VehicleInspectionCreate) => {
    if (editingInspection) {
      updateMutation.mutate({ id: editingInspection.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const handleExportClick = () => {
    if (!data || data.inspections.length === 0) {
      setExportResult({
        success: false,
        message: 'No pending inspections to export',
      });
      return;
    }
    setExportDialogOpen(true);
  };

  const handleExportConfirm = () => {
    setExportDialogOpen(false);
    exportMutation.mutate();
  };

  const handleCloseExportResult = () => {
    setExportResult(null);
  };

  const handleCloseSaveResult = () => {
    setSaveResult(null);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <LocalShippingIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Vehicle Inspections
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Inspect and track required paperwork and equipment for trucks and trailers.
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUnit}
            >
              Add Unit
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleExportClick}
              disabled={!data || data.inspections.length === 0 || exportMutation.isLoading}
            >
              {exportMutation.isLoading ? 'Exporting...' : 'Export & Email Report'}
            </Button>
          </Box>

          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Failed to load vehicle inspections. Please try again.
            </Alert>
          )}

          {/* Save Result Alert */}
          {saveResult && (
            <Alert
              severity={saveResult.success ? 'success' : 'error'}
              sx={{ mb: 3 }}
              onClose={handleCloseSaveResult}
            >
              {saveResult.message}
            </Alert>
          )}

          {/* Export Result Alert */}
          {exportResult && (
            <Alert
              severity={exportResult.success ? 'success' : 'error'}
              sx={{ mb: 3 }}
              onClose={handleCloseExportResult}
            >
              {exportResult.message}
            </Alert>
          )}

          {/* Inspections Table */}
          {!isLoading && !error && data && (
            <VehicleInspectionTable
              inspections={data.inspections}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}

          {/* Add/Edit Form Modal */}
          <VehicleInspectionForm
            open={formOpen}
            onClose={() => {
              setFormOpen(false);
              setEditingInspection(null);
            }}
            onSubmit={handleFormSubmit}
            inspection={editingInspection || undefined}
            isLoading={createMutation.isLoading || updateMutation.isLoading}
          />

          {/* Export Confirmation Dialog */}
          <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
            <DialogTitle>Confirm Export</DialogTitle>
            <DialogContent>
              This will export {data?.inspections.length || 0} vehicle inspection(s) and email
              the report. After export, these inspections will be marked as exported and removed
              from the pending list. Continue?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleExportConfirm} variant="contained">
                Export & Email
              </Button>
            </DialogActions>
          </Dialog>
        </>
      </Box>
    </Container>
  );
};

export default VehicleDocs;
